import React from "react";
import { Card, Col, Row, Tabs, Typography } from "antd";
import { useSearchParams } from "react-router-dom";

import { StudioClipKind, StudioClipStatus } from "@fitness-app/data-models/entities/StudioClip";

import { AdsPanel } from "./components/AdsPanel";
import { ClipsTable } from "./components/ClipsTable";
import { MusicPanel } from "./components/MusicPanel";
import { SourcesPanel } from "./components/SourcesPanel";
import { useStudioClips, useStudioRealtime } from "./hooks/useStudioData";

const READY_STATUSES = new Set([StudioClipStatus.Approved, StudioClipStatus.Published]);

const DEFAULT_TAB = "production";
/** Zakładka siedzi w URL-u, żeby dało się wejść prosto do wyników — np. z drawera zadań AI. */
const TAB_KEYS = new Set([DEFAULT_TAB, "ads", "music", "ready"]);

/**
 * Panel „Studio" (TRE-1975/TRE-1983) — wspólny dom dla produkcji contentu: dziś Klipownia
 * (nagranie → kandydaci → render + QC → akceptacja → gotowce), docelowo także kreacje
 * reklamowe z TRE-1658. Human-in-the-loop jest wymogiem: nic nie wychodzi na zewnątrz bez
 * decyzji człowieka w tym panelu.
 */
export const Studio = (): React.ReactElement => {
  useStudioRealtime();
  const clipsQuery = useStudioClips();
  const [searchParams, setSearchParams] = useSearchParams();

  const requestedTab = searchParams.get("tab");
  const activeTab = requestedTab && TAB_KEYS.has(requestedTab) ? requestedTab : DEFAULT_TAB;
  const changeTab = (key: string) =>
    setSearchParams(
      (params) => {
        if (key === DEFAULT_TAB) params.delete("tab");
        else params.set("tab", key);
        return params;
      },
      { replace: true },
    );

  const allClips = (clipsQuery.data ?? []).filter((clip) => clip.kind !== StudioClipKind.Variant);
  const readyClips = allClips.filter((clip) => READY_STATUSES.has(clip.status));

  return (
    <Row className="p-6">
      <Col xs={24}>
        <Card title="Studio">
          <Tabs
            activeKey={activeTab}
            onChange={changeTab}
            items={[
              {
                key: "production",
                label: "Produkcja klipów",
                children: (
                  <div className="flex flex-col gap-6">
                    <SourcesPanel />
                    <div>
                      <Typography.Title level={5}>Klipy</Typography.Title>
                      <ClipsTable clips={allClips} loading={clipsQuery.isLoading} />
                    </div>
                  </div>
                ),
              },
              {
                key: "ads",
                label: "Reklamy",
                children: <AdsPanel />,
              },
              {
                key: "music",
                label: "Muzyka",
                children: <MusicPanel />,
              },
              {
                key: "ready",
                label: `Zaakceptowane (${readyClips.length})`,
                children: <ClipsTable clips={readyClips} loading={clipsQuery.isLoading} readonly />,
              },
            ]}
          />
        </Card>
      </Col>
    </Row>
  );
};
